import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneRight, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AssistantAnswer from './AssistantAnswer';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SCOPES = [
  { value: 'document', label: 'この書類のみ' },
  { value: 'folder', label: 'このフォルダー' },
  { value: 'all', label: 'すべての書類' },
];

/**
 * UX-2.5A · 「この書類について質問」— 同じ Beta-3 バックエンド・同じ権限・同じ出典を
 * 使う、書類ごとの入口。2つ目のアシスタントではありません。
 * 対象（スコープ）は常に画面に表示し、勝手に広がりません。
 */
export default function DocAskPanel({ documentId, folderId, documentTitle, onClose, onOpenDoc }) {
  const [scope, setScope] = useState('document');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const ask = async () => {
    if (!question.trim()) { toast.error('質問を入力してください'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/document-assistant/ask`, {
        question: question.trim(), scope_type: scope,
        previous_answer: res ? (res.narrative || res.answer || res.answer_ja) : null,
        answer_language: res?.language || null,
        document_id: scope === 'document' ? documentId : null,
        folder_id: scope === 'folder' ? folderId : null,
        limit: 6,
      }, { headers: authHeader() });
      setRes(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '回答の取得に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <aside role="dialog" aria-label="この書類について質問"
      data-testid="doc-ask-panel"
      className="w-full sm:w-[420px] shrink-0 h-full bg-white border-l border-zinc-200 flex flex-col">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-zinc-100">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">この書類について質問</p>
          <p className="text-xs text-zinc-500 truncate" title={documentTitle}>{documentTitle}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="質問パネルを閉じる" title="閉じる"
          data-testid="doc-ask-close"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-500 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none">
          <X size={16} />
        </button>
      </div>

      {/* 対象（スコープ）は常時表示。勝手に広がらない */}
      <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/70">
        <p className="text-xs text-zinc-500 mb-1.5">
          対象 <span className="text-zinc-400">（変更すると探す範囲が変わります）</span>
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="doc-ask-scope">
          {SCOPES.map((s) => (
            <button key={s.value} type="button" onClick={() => { setScope(s.value); setRes(null); }}
              aria-pressed={scope === s.value}
              disabled={s.value === 'folder' && !folderId}
              data-testid={`doc-ask-scope-${s.value}`}
              className={`inline-flex items-center rounded-full border px-3 min-h-[32px] text-xs font-medium transition-colors disabled:opacity-40 ${
                scope === s.value
                  ? 'bg-teal-700 text-white border-teal-700'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {s.value === scope && <span className="sr-only">選択中: </span>}{s.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-teal-800 mt-2" data-testid="doc-ask-scope-active">
          {SCOPES.find((s) => s.value === scope)?.label} を対象に探します
        </p>
      </div>

      <div className="p-4 flex items-center gap-2 border-b border-zinc-100">
        <Input value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) ask(); }}
          placeholder="例: 材質はなんですか"
          className="h-10 text-sm" data-testid="doc-ask-input" />
        <Button onClick={ask} disabled={busy} aria-label="質問する"
          className="h-10 px-3 bg-teal-700 hover:bg-teal-800" data-testid="doc-ask-send">
          <PaperPlaneRight size={15} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {busy && <p className="text-sm text-zinc-400">書類を読んでいます…</p>}
        {res && (
          <>
            <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600"
              data-testid="doc-ask-answer-scope">{res.scope_target || res.scope_target_ja}</span>
            <AssistantAnswer result={res} compact onOpenDoc={onOpenDoc} />
          </>
        )}
        {!res && !busy && (
          <p className="text-sm text-zinc-400">
            この書類の内容について質問できます。回答には必ず出典が付きます。
          </p>
        )}
      </div>
    </aside>
  );
}
